import React from 'react';
import { Buildings } from '@phosphor-icons/react';
import { FilterSwitch } from '../design-system/FilterSwitch';
import { useCompanyGroupingPreference } from '../contexts/CompanyGroupingPreferenceContext';
import { useFormatMessage } from '../i18n-review/client/Msg';

/**
 * Page-level control for switching the SRP between listing every job as its own card (default,
 * temporarily disabled grouping) and grouping a company's multiple open roles into one
 * HitCompanyGroupCard (see utils/srp/group-hits-by-company.ts). Client-only preference (see
 * CompanyGroupingPreferenceContext) — renders the same "ungrouped" state during SSR/first paint
 * as every visitor gets, so no hydration mismatch even though a returning visitor's stored choice
 * applies right after mount.
 */
export function CompanyGroupingToggle({ className = '' }: { className?: string }): React.ReactElement {
  const formatMessage = useFormatMessage();
  const { mode, toggle } = useCompanyGroupingPreference();
  const isGrouped = mode === 'grouped';
  const label = formatMessage({
    id: 'companyGroupingToggle.label',
    defaultMessage: 'Group jobs by company',
    description: 'Label for the page-level toggle switching search results between one card per job and grouped cards per company',
  });

  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      <Buildings className="h-4 w-4 text-[#7A6E5F] dark:text-[#B8A48C]" aria-hidden />
      <label htmlFor="company-grouping-toggle" className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882] select-none">
        {label}
      </label>
      <FilterSwitch id="company-grouping-toggle" checked={isGrouped} onCheckedChange={toggle} aria-label={label} />
    </div>
  );
}
